import { getApiBaseUrl } from '../config/env';
import { mapPublicFaqsPayload } from './mapPublicFaqs';

const CACHE_KEY = 'jobscribe_public_faqs_v1';
const CACHE_TTL_MS = 10 * 60 * 1000;

/** @type {Promise<{ landingFaqs: Array, faqCategories: Array }> | null} */
let inflightRequest = null;

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.fetchedAt) return null;
    if (Date.now() - parsed.fetchedAt > CACHE_TTL_MS) return null;
    return mapPublicFaqsPayload(parsed);
  } catch {
    return null;
  }
}

function writeCache(payload) {
  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        fetchedAt: Date.now(),
        landing: payload.landingFaqs,
        categories: payload.faqCategories,
      })
    );
  } catch {
    // ignore
  }
}

export function getCachedPublicFaqs() {
  return readCache();
}

function hasFaqContent({ landingFaqs, faqCategories }) {
  return landingFaqs.length > 0 || faqCategories.length > 0;
}

/**
 * @param {{ forceRefresh?: boolean }} [options]
 * @returns {Promise<{ landingFaqs: Array, faqCategories: Array }>}
 */
export async function fetchPublicFaqs(options = {}) {
  const { forceRefresh = false } = options;

  if (!forceRefresh) {
    const cached = readCache();
    if (cached && hasFaqContent(cached)) return cached;
  }

  if (inflightRequest) return inflightRequest;

  inflightRequest = (async () => {
    const res = await fetch(`${getApiBaseUrl()}/public/faqs`, {
      credentials: 'omit',
    });
    if (!res.ok) {
      throw new Error('Failed to fetch FAQs');
    }
    const json = await res.json();
    const data = json.data ?? json;
    const payload = mapPublicFaqsPayload(data);
    if (hasFaqContent(payload)) {
      writeCache(payload);
    }
    return payload;
  })();

  try {
    return await inflightRequest;
  } finally {
    inflightRequest = null;
  }
}

export function prefetchPublicFaqs() {
  const cached = readCache();
  if (cached && hasFaqContent(cached)) return;
  void fetchPublicFaqs().catch(() => {});
}
